/**
 * Topic Node Renderer
 *
 * Renders topic-type nodes (category/grouping nodes) with LOD support:
 * - full: Title with optional description and count
 * - title: Bold title only
 * - icon: Colored circle with initial
 * - dot: Colored dot
 *
 * Phase 0.7: Supports WebGL texture caching for distant nodes
 */

import type { UINode, ScreenMetrics, WorldRect } from '../types';
import type { NodeRenderer, RenderLayers, RenderResult, LODLevel } from '../renderer';
import { calculateLOD } from '../renderer';
import { hashContent } from '../texture-cache';

/** Default texture threshold in screen pixels */
const DEFAULT_TEXTURE_THRESHOLD = 60;

/**
 * Topic renderer for category/grouping nodes.
 */
export class TopicRenderer implements NodeRenderer {
	private lastLOD: Map<string, LODLevel> = new Map();
	private lastContent: Map<string, string> = new Map();
	private contentHashes: Map<string, string> = new Map();

	render(node: UINode, metrics: ScreenMetrics, layers: RenderLayers): RenderResult {
		const screenSize = Math.max(metrics.width, metrics.height);
		const opts = layers.options;

		// 1. Calculate LOD first - this is independent of texture state
		// forceLod takes precedence over everything
		const lod: LODLevel = opts.forceLod
			? opts.forceLod
			: opts.simplifyLOD
				? calculateLOD(metrics, {
						enableLodFull: opts.enableLodFull,
						enableLodTitle: opts.enableLodTitle,
						enableLodIcon: opts.enableLodIcon,
						enableLodDot: opts.enableLodDot
					})
				: 'full';

		// 2. Calculate content hash for texture cache invalidation
		// Include LOD level in the hash so different LODs get different textures
		const baseContentHash = this.getContentHash(node);
		const contentHash = `${baseContentHash}:${lod}`;

		// 3. Compound texture ID includes LOD level
		const textureId = `${node.id}:${lod}`;

		// 4. Determine if we should use texture rendering
		// forceTextureRendering forces texture mode regardless of screen size
		const textureThreshold = opts.textureThreshold ?? DEFAULT_TEXTURE_THRESHOLD;
		const wantTexture = (opts.forceTextureRendering || screenSize < textureThreshold) && layers.webgl !== null;

		// 5. Check if we have a valid cached texture for this LOD level
		const hasValidTexture = wantTexture && layers.webgl?.hasValidTexture(textureId, contentHash);

		const lastLod = this.lastLOD.get(node.id);
		const lodChanged = lastLod !== lod;

		// Check if this is a newly created element (e.g., re-entered frustum)
		const isNewElement = layers.html.isNewlyCreated(node.id);

		// Update LOD data attribute
		layers.html.setDataAttribute(node.id, 'lod', lod);

		// Try texture rendering if we have a valid texture for this LOD level
		if (hasValidTexture && layers.webgl) {
			const worldRect: WorldRect = {
				x: node.position.x,
				y: node.position.y,
				width: node.size.width,
				height: node.size.height
			};

			// Rotation is applied via shader (texture is captured without rotation for efficiency)
			const textureDrawn = layers.webgl.drawCachedTexture(textureId, worldRect, 1, node.rotation ?? 0);

			if (textureDrawn) {
				// Successfully drew texture - hide HTML content
				layers.html.setContent(node.id, '');
				layers.html.addClass(node.id, 'texture-cached');

				// Clear lastContent so content is re-rendered when switching back to HTML
				this.lastContent.delete(node.id);

				this.lastLOD.set(node.id, lod);
				return { lod, changed: lodChanged };
			}
		}

		// Fall back to HTML rendering
		layers.html.removeClass(node.id, 'texture-cached');

		// Render content based on LOD (this happens FIRST, before texture capture)
		const content = this.renderContent(node, lod);
		const lastContentValue = this.lastContent.get(node.id);
		const contentChanged = lastContentValue !== content || isNewElement;

		if (contentChanged) {
			layers.html.setContent(node.id, content);
			this.lastContent.set(node.id, content);
		}

		// Request texture generation AFTER HTML content is rendered
		// Pass expectedLOD to guard against race condition where LOD changes during async capture
		if (wantTexture && layers.webgl && !hasValidTexture) {
			layers.webgl.requestTexture(
				textureId,
				() => layers.html.getContainer(node.id),
				contentHash,
				1,
				lod // expectedLOD for race condition validation
			);
		}

		this.lastLOD.set(node.id, lod);

		return { lod, changed: lodChanged || contentChanged };
	}

	/**
	 * Get a content hash for cache invalidation.
	 */
	private getContentHash(node: UINode): string {
		const cached = this.contentHashes.get(node.id);
		if (cached) {
			return cached;
		}

		const contentStr = JSON.stringify(node.data);
		const hash = hashContent(contentStr);
		this.contentHashes.set(node.id, hash);
		return hash;
	}

	private renderContent(node: UINode, lod: LODLevel): string {
		const title = this.getTitle(node);
		const color = this.getColor(node);
		const count = this.getCount(node);
		const initial = title.charAt(0).toUpperCase();

		switch (lod) {
			case 'full':
				return `
					<div class="topic-node topic-full" style="--topic-color: ${color}">
						<div class="node-close-btn" data-action="close">&times;</div>
						<div class="topic-header">
							<span class="topic-badge" style="background: ${color}">${initial}</span>
							<span class="topic-title">${this.escapeHtml(title)}</span>
							${count > 0 ? `<span class="topic-count">${count}</span>` : ''}
						</div>
						${this.getDescription(node) ? `<div class="topic-description">${this.escapeHtml(this.getDescription(node))}</div>` : ''}
					</div>
				`;

			case 'title':
				return `
					<div class="topic-node topic-title-only" style="--topic-color: ${color}">
						<span class="topic-badge-small" style="background: ${color}">${initial}</span>
						<span class="topic-title">${this.escapeHtml(title)}</span>
					</div>
				`;

			case 'icon':
				return `
					<div class="topic-node topic-icon-only" style="--topic-color: ${color}">
						<span class="topic-badge-large" style="background: ${color}">${initial}</span>
					</div>
				`;

			case 'dot':
				return `<div class="topic-node topic-dot" style="background: ${color}"></div>`;
		}
	}

	private getTitle(node: UINode): string {
		return (node.data.title as string) || (node.data.label as string) || 'Topic';
	}

	private getDescription(node: UINode): string {
		return (node.data.description as string) || '';
	}

	private getColor(node: UINode): string {
		// Use node color or generate from title
		if (node.data.color) return node.data.color as string;

		// Generate color from title hash
		const title = this.getTitle(node);
		let hash = 0;
		for (let i = 0; i < title.length; i++) {
			hash = title.charCodeAt(i) + ((hash << 5) - hash);
		}

		// HSL color with fixed saturation and lightness
		const hue = Math.abs(hash % 360);
		return `hsl(${hue}, 65%, 50%)`;
	}

	private getCount(node: UINode): number {
		return (node.data.count as number) || (node.data.childCount as number) || 0;
	}

	private escapeHtml(text: string): string {
		return text
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;');
	}

	getPreferredSize(node: UINode): { width: number; height: number } {
		return {
			width: node.size?.width ?? 180,
			height: node.size?.height ?? 80
		};
	}

	cleanup(nodeId: string): void {
		this.lastLOD.delete(nodeId);
		this.lastContent.delete(nodeId);
		this.contentHashes.delete(nodeId);
	}
}
